import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAnalyticsConsent, useConsentPreferencesOpen } from '../hooks/useAnalyticsConsent';
import {
  closeConsentPreferences,
  setConsentChoice,
  syncStoredConsent,
  type ConsentChoice,
} from '../lib/analyticsConsent';

/**
 * Consent banner and preference panel (#125). The choice lives in lib/analyticsConsent so
 * the banner, the footer entry and page tracking always agree. The banner shows while no
 * valid choice exists, and again whenever the visitor reopens preferences from the footer.
 */
export function CookieConsentBanner() {
  const { t } = useLanguage();
  const consent = useAnalyticsConsent();
  const preferencesOpen = useConsentPreferencesOpen();
  const headingRef = useRef<HTMLParagraphElement>(null);

  // Returning visitor: re-apply the stored choice to Google Consent Mode.
  useEffect(() => {
    syncStoredConsent();
  }, []);

  const isVisible = consent === 'unset' || preferencesOpen;
  const reopened = preferencesOpen && consent !== 'unset';

  // When reopened from the footer, move focus into the panel so keyboard and screen-reader
  // users land on it; Escape closes it without changing the choice. Focus returns to the
  // element that opened it (the footer entry) when the panel closes, however it closes.
  useEffect(() => {
    if (!reopened) return;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    headingRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeConsentPreferences();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      if (opener && opener !== document.body && opener.isConnected) opener.focus();
    };
  }, [reopened]);

  if (!isVisible) return null;

  const handleChoice = (choice: ConsentChoice) => setConsentChoice(choice);

  return (
    <section
      aria-labelledby="cookie-consent-title"
      data-testid="cookie-consent"
      data-toast-bottom-surface=""
      className="fixed inset-x-0 bottom-0 z-[80] px-4 pb-4 sm:px-6 sm:pb-6"
    >
      <div className="mx-auto flex max-w-5xl flex-col gap-4 rounded-lg border border-white/10 bg-slate-950/95 p-4 shadow-2xl shadow-black/40 backdrop-blur md:flex-row md:items-center md:justify-between">
        <div className="max-w-3xl">
          <p
            id="cookie-consent-title"
            ref={headingRef}
            tabIndex={-1}
            className="text-sm font-semibold text-white focus:outline-none"
          >
            {t('consent_title')}
          </p>
          <p className="mt-1 text-sm leading-6 text-slate-300">
            {t('consent_body')}{' '}
            <Link to="/gizlilik-politikasi" className="font-semibold text-brand-300 underline-offset-4 hover:underline">
              {t('consent_privacy_link')}
            </Link>{' '}
            {t('consent_body_after')}
          </p>
          {reopened && (
            <p role="status" className="mt-1 text-sm text-slate-400">
              {consent === 'accepted' ? t('consent_current_accepted') : t('consent_current_rejected')}
            </p>
          )}
        </div>
        <div className="flex shrink-0 flex-col gap-2 sm:flex-row">
          {reopened && (
            <button
              type="button"
              onClick={closeConsentPreferences}
              className="rounded-md px-4 py-2 text-sm font-semibold text-slate-300 transition hover:text-white focus:outline-none focus:ring-2 focus:ring-brand-400"
            >
              {t('consent_close')}
            </button>
          )}
          <button
            type="button"
            onClick={() => handleChoice('rejected')}
            className="rounded-md border border-white/15 px-4 py-2 text-sm font-semibold text-slate-100 transition hover:border-white/30 hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-brand-400"
          >
            {t('consent_reject')}
          </button>
          <button
            type="button"
            onClick={() => handleChoice('accepted')}
            className="rounded-md bg-brand-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-300"
          >
            {t('consent_accept')}
          </button>
        </div>
      </div>
    </section>
  );
}
